import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
  Button,
  Chip,
} from '@mui/material';
import { 
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { balanceApi } from '../services/balanceService.ts';
import { balancePrintService, PrintBalanceData } from '../services/printService.ts';
import { employeeApi, Employee } from '../services/employeeService.ts';

const NewBalanceFormsPage: React.FC = () => {
  const { sessionToken } = useAuth();
  const { showError } = useSnackbar();

  // State for table data and filters
  const [dates, setDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchDate, setSearchDate] = useState<string>('');

  // Per-date AM/PM Cash Due and Net Sales totals, keyed by date. Fetched
  // separately since getDistinctDates only returns the date itself.
  interface DateTotals {
    amCashDue: number;
    pmCashDue: number;
    amNetSales: number;
    pmNetSales: number;
    totalNetSales: number;
  }
  const [dateTotals, setDateTotals] = useState<Record<string, DateTotals>>({});

  // Ref to prevent duplicate API calls
  const hasLoadedRef = useRef(false);

  // Fetch each listed date's balance entries and roll them up into AM/PM Cash Due
  // and Net Sales totals. Dates with no entries default to $0.00 in the row renderer.
  const fetchDateTotals = async (dateList: string[]) => {
    if (!sessionToken || dateList.length === 0) return;

    try {
      const results = await Promise.all(
        dateList.map(date => balanceApi.getBalanceByDate(sessionToken, date.slice(0, 10)))
      );

      const totalsByDate: Record<string, DateTotals> = {};
      dateList.forEach((date, idx) => {
        const entries = results[idx]?.balance || [];
        const amEntries = entries.filter(b => b.shift === 'AM');
        const pmEntries = entries.filter(b => b.shift === 'PM');

        const amCashDue = amEntries.reduce((sum, b) => sum + (Number(b.cashDue) || 0), 0);
        const pmCashDue = pmEntries.reduce((sum, b) => sum + (Number(b.cashDue) || 0), 0);
        const amNetSales = amEntries.reduce((sum, b) => sum + (Number(b.netSales) || 0), 0);
        const pmNetSales = pmEntries.reduce((sum, b) => sum + (Number(b.netSales) || 0), 0);

        totalsByDate[date] = {
          amCashDue,
          pmCashDue,
          amNetSales,
          pmNetSales,
          totalNetSales: amNetSales + pmNetSales,
        };
      });

      setDateTotals(totalsByDate);
    } catch (error) {
      console.error('Error fetching date totals:', error);
    }
  };

  // Fetch distinct dates from API
  const fetchDates = async () => {
    if (!sessionToken) return;

    try {
      setLoading(true);
      const response = await balanceApi.getDistinctDates(sessionToken);
      setDates(response);
      hasLoadedRef.current = true;
      fetchDateTotals(response);
    } catch (error) {
      console.error('Error fetching balance dates:', error);
      showError('Failed to fetch balance dates');
    } finally {
      setLoading(false);
    }
  };

  // Handle date search
  const handleDateSearch = () => {
    if (!searchDate) {
      fetchDates();
      return;
    }
    
    // Filter dates by search date
    if (!sessionToken) return;
    
    try {
      setLoading(true);
      balanceApi.getDistinctDates(sessionToken).then(response => {
        const filtered = response.filter(date => date.includes(searchDate));
        setDates(filtered);
        fetchDateTotals(filtered);
      });
    } catch (error) {
      console.error('Error searching dates:', error);
      showError('Failed to search dates');
    } finally {
      setLoading(false);
    }
  };

  // Handle clear filter
  const handleClearFilter = () => {
    setSearchDate('');
    fetchDates();
  };

  // Handle view button click - opens printable page
  const handleViewBalance = async (date: string) => {
    if (!sessionToken) {
      showError('No session token available');
      return;
    }

    try {
      setLoading(true);
      // The date from getDistinctDates is already in YYYY-MM-DD format
      // But we need to ensure it's properly formatted
      let onlyDate = date;
      if (date.includes('T')) {
        onlyDate = date.split('T')[0];
      } else if (date.length > 10) {
        onlyDate = date.slice(0, 10);
      }
      
      if (!onlyDate || onlyDate.length !== 10) {
        showError('Invalid date format');
        return;
      }
      
      console.log('Fetching balance data for date:', onlyDate);
      
      // Fetch balance data for the specific date
      const balanceData = await balanceApi.getBalanceByDate(sessionToken, onlyDate);
      console.log('Balance data received:', balanceData);
      
      // Fetch employees to resolve names
      const employeesResp = await employeeApi.getEmployees({ page: 1, limit: 1000 }, sessionToken);
      const employeeMap = new Map<number, Employee>(employeesResp.data.map(e => [e.id, e]));
      
      // Convert API data to print format
      const printData: PrintBalanceData = {
        date: onlyDate,
        balance: {
          am: (balanceData.balance || [])
            .filter(b => b.shift === 'AM')
            .map(b => {
              const emp = employeeMap.get(b.employeeId);
              return {
                employeeName: emp?.name || `Employee ${b.employeeId}`,
                employeeCode: emp?.employeeId,
                type: b.type,
                cashDue: Number(b.cashDue) || 0,
                netSales: Number(b.netSales) || 0,
              };
            }),
          pm: (balanceData.balance || [])
            .filter(b => b.shift === 'PM')
            .map(b => {
              const emp = employeeMap.get(b.employeeId);
              return {
                employeeName: emp?.name || `Employee ${b.employeeId}`,
                employeeCode: emp?.employeeId,
                type: b.type,
                cashDue: Number(b.cashDue) || 0,
                netSales: Number(b.netSales) || 0,
              };
            }),
        },
        paid_outs: (balanceData.paid_outs || []).map(po => ({
          description: po.description || '',
          cashDue: Number(po.cashDue) || 0,
        })),
        busser_tips: (balanceData.busser_tips || []).map(bt => ({
          busserName: bt.busserName || '',
          tips: Number(bt.tips) || 0,
        })),
        event_servers_bartender_tips: (balanceData.event_servers_bartender_tips || []).map(es => ({
          eventServerBartender: es.eventServerBartender || '',
          pooledTips: Number(es.pooledTips) || 0,
        })),
        negative_cash_outs: (balanceData.negative_cash_outs || []).map(nc => ({
          tookFrom: nc.tookFrom || '',
          negativeCashOuts: Number(nc.negativeCashOuts) || 0,
        })),
        keno_cash: (balanceData.keno_cash || []).map(kc => {
          const emp = employeeMap.get(kc.employeeId);
          return {
            employeeName: emp?.name || `Employee ${kc.employeeId}`,
            employeeCode: emp?.employeeId,
            kenoCash: Number(kc.kenoCash) || 0,
          };
        }),
      };

      console.log('Print data prepared:', printData);

      // Open print dialog
      try {
        balancePrintService.openPrintDialog(printData);
      } catch (printError: any) {
        console.error('Error opening print dialog:', printError);
        throw new Error(`Failed to open print dialog: ${printError?.message || 'Unknown error'}`);
      }
      
    } catch (error: any) {
      console.error('Error generating balance report:', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        response: error?.response,
        data: error?.response?.data,
      });
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to generate balance report';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    if (sessionToken && !hasLoadedRef.current) {
      fetchDates();
    }
    
    // Cleanup function to reset ref when component unmounts
    return () => {
      hasLoadedRef.current = false;
    };
  }, [sessionToken]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h6" sx={{ color: '#7f0000', fontWeight: 700, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
            New Balance Forms
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage balance entries by date
          </Typography>
        </Box>
      </Box>

      {/* Search Filter */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Search & Filter
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Search by Date"
            type="date"
            value={searchDate}
            onChange={(e) => setSearchDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleDateSearch}
            disabled={loading}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={handleClearFilter}
            disabled={loading}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Summary Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #9e9e9e' }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 2 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                {['Date', 'AM Cash Due', 'PM Cash Due', 'AM Net Sales', 'PM Net Sales', 'Total Net Sales', 'Actions'].map((h, i) => (
                  <TableCell key={h} align={h === 'Date' ? 'left' : h === 'Actions' ? 'center' : 'right'} sx={{
                    color: '#fff', fontWeight: 700, fontSize: '0.82rem', letterSpacing: '0.04em',
                    textTransform: 'uppercase', border: '2px solid #7f0000', py: 1.5, px: 2,
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, border: '2px solid #9e9e9e' }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" sx={{ mt: 1 }}>Loading balance data...</Typography>
                  </TableCell>
                </TableRow>
              ) : dates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, border: '2px solid #9e9e9e', color: 'text.secondary' }}>
                    No balance data found
                  </TableCell>
                </TableRow>
              ) : (
                dates.map((date, idx) => {
                  const totals = dateTotals[date];
                  return (
                    <TableRow key={date} sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fdf5f5', '&:hover': { backgroundColor: '#fce4e4' } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{formatDate(date)}</Typography>
                      </TableCell>
                      <TableCell align="right">${(totals?.amCashDue ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${(totals?.pmCashDue ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${(totals?.amNetSales ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${(totals?.pmNetSales ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${(totals?.totalNetSales ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Chip icon={<VisibilityIcon />} label="View" color="primary" size="small" variant="outlined"
                          onClick={() => handleViewBalance(date)} sx={{ cursor: 'pointer' }} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default NewBalanceFormsPage;

